'use client'

import { cn } from '@/lib/utils'
import { Typo3Image } from '@/types'
import { Icon } from '@/ui/components'
import { MEDIA } from '@spot'
import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { memo, useCallback, useEffect, useState } from 'react'
import Img from '../Img/Img'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data?: Typo3Image[]
  emblaOptions?: EmblaOptionsType
}

const SliderImage = ({ data, emblaOptions, ...props }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ ...emblaOptions })
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [disabledPrev, setDisabledPrev] = useState<boolean>(false)

  const checkDisabled = useCallback(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.canScrollPrev() ? setDisabledPrev(false) : setDisabledPrev(true)
    emblaApi.canScrollNext() ? setDisabledNext(false) : setDisabledNext(true)
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', checkDisabled)
      emblaApi.on('init', checkDisabled)
    }
  }, [emblaApi, checkDisabled])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  if (!data?.length) {
    return null
  }
  return (
    <div
      className={cn('embla', 'relative col-span-12')}
      ref={emblaRef}
      {...props}
    >
      <div className="grid auto-cols-auto grid-flow-col gap-x-6 md:auto-cols-[100%]">
        {data?.map((img, index) => (
          <div key={index?.toString()}>
            <Img
              data={img}
              fill
              objectFit
              className={cn(
                img.properties?.dimensions?.width &&
                  img.properties?.dimensions?.height &&
                  img.properties?.dimensions?.width <
                    img.properties?.dimensions?.height
                  ? 'h-[31.75rem] w-[23rem] md:h-[25.5rem] md:w-full'
                  : 'h-[31.75rem] w-[55.5rem] md:h-[25.5rem] md:w-full'
              )}
              sizes={`${MEDIA.tablet} 100vw, 50rem`}
              quality={90}
            />
            <div
              className={cn(
                'text-small',
                'max-sm:px-gutter mt-4 h-5 text-neutral-400 max-sm:p-0'
              )}
            >
              {img.properties?.description !== ''
                ? img.properties?.description
                : img.properties?.title}
            </div>
          </div>
        ))}
      </div>
      <div className="absolute top-[28.75rem] right-0 z-[5] grid grid-cols-2 backdrop-blur-[12px] md:top-[22.5rem]">
        <button
          className="flex h-12 w-12 cursor-pointer flex-col items-center justify-center bg-neutral-100 transition-[background-color,opacity] duration-[var(--duration-default)] ease-[var(--ease-out-cubic)] hover:bg-neutral-200 data-[disable=true]:pointer-events-none data-[disable=true]:bg-transparent data-[disable=true]:opacity-70 hover:[&_.icon-arrow]:scale-110"
          onClick={scrollPrev}
          data-disable={disabledPrev}
        >
          <Icon
            className="icon-arrow h-5 w-5 transition-transform duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]"
            icon="material-symbols:arrow-back-ios-new"
          />
        </button>
        <button
          data-disable={disabledNext}
          className="flex h-12 w-12 cursor-pointer flex-col items-center justify-center bg-neutral-100 transition-[background-color,opacity] duration-[var(--duration-default)] ease-[var(--ease-out-cubic)] hover:bg-neutral-200 data-[disable=true]:pointer-events-none data-[disable=true]:bg-transparent data-[disable=true]:opacity-70 hover:[&_.icon-arrow]:scale-110"
          onClick={scrollNext}
          data-disabled={emblaApi?.canScrollPrev()}
        >
          <Icon
            className="icon-arrow h-5 w-5 transition-transform duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]"
            icon="material-symbols:arrow-forward-ios"
          />
        </button>
      </div>
    </div>
  )
}

export default memo(SliderImage)
